/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { Link } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'
import { type ComponentProps, type CSSProperties, type ReactNode, useEffect, useState } from 'react'

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'

interface SplitFeatureSectionImage {
  src: string
  alt: string
}

/** vite-imagetools `&as=picture` shape (`format=avif;webp&as=picture`, etc.) */
export interface SplitFeatureSectionPicture {
  sources: Record<string, string>
  img: { src: string; w: number; h: number }
}

export interface SplitFeatureSectionProps {
  eyebrow: string
  heading: string
  body: ReactNode
  imageSrc?: string
  imageAlt?: string
  images?: SplitFeatureSectionImage[]
  carouselAutoplay?: boolean
  carouselShowControls?: boolean
  imageSide?: 'left' | 'right'
  footerLabel?: string
  footerTo?: string
  footer?: ReactNode
  imageProps?: ComponentProps<'img'>
  eyebrowProps?: ComponentProps<'p'>
  headingProps?: ComponentProps<'h2'>
  bodyProps?: ComponentProps<'div'>
  footerProps?: ComponentProps<'div'>
  footerLinkProps?: Omit<ComponentProps<typeof Link>, 'to' | 'children'>
  /** When set (single image only), media is wrapped in `<picture>` with responsive `source` elements. */
  imagePicture?: SplitFeatureSectionPicture
  /** `sizes` for `<source>` in `imagePicture` (default matches half column on `md+`). */
  imagePictureSizes?: string
  /** CSS `object-position` for `object-cover` framing (e.g. `center 22%` biases the crop toward the top). */
  imageObjectPosition?: string
}

const DEFAULT_IMAGE_SIDE = 'left'

function SplitFeatureCarouselDots({ slideCount }: { slideCount: number }) {
  const { api } = useCarousel()
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (!api) {
      return
    }
    const sync = () => setSelected(api.selectedScrollSnap())
    sync()
    api.on('select', sync)
    return () => {
      api.off('select', sync)
    }
  }, [api])

  return (
    <div className="absolute right-0 bottom-6 left-0 z-10 flex justify-center gap-1.5">
      {Array.from({ length: slideCount }, (_, i) => (
        <button
          aria-label={`Go to slide ${i + 1}`}
          className={cn(
            'h-2 w-2 origin-center rounded-full bg-white opacity-50 transition-[width,opacity] duration-200',
            selected === i && 'w-10 opacity-100'
          )}
          key={i}
          onClick={() => api?.scrollTo(i)}
          type="button"
        />
      ))}
    </div>
  )
}

const carouselNavButtonClass =
  'left-3 top-1/2 z-10 -translate-y-1/2 size-8 border bg-black/70 text-white hover:bg-black/80 disabled:opacity-40'

const carouselNavButtonClassNext =
  'right-3 top-1/2 left-auto z-10 -translate-y-1/2 size-8 border bg-black/70 text-white hover:bg-black/80 disabled:opacity-40'

/** Fixed frame height so media does not grow/shrink with column width. */
const mediaImageFrameClass = 'relative h-60 md:h-75 w-full overflow-hidden'

const mediaImgClass = 'absolute inset-0 size-full object-cover object-center'

export function SplitFeatureSection({
  eyebrow,
  heading,
  body,
  imageSrc,
  imageAlt,
  images,
  carouselAutoplay = false,
  carouselShowControls = true,
  imageSide = DEFAULT_IMAGE_SIDE,
  footerLabel,
  footerTo,
  footer,
  imageProps,
  eyebrowProps,
  headingProps,
  bodyProps,
  footerProps,
  footerLinkProps,
  imagePicture,
  imagePictureSizes = '(min-width: 768px) 50vw, 100vw',
  imageObjectPosition,
}: SplitFeatureSectionProps) {
  const isImageLeft = imageSide === 'left'
  const hasDefaultFooter = !footer && footerLabel && footerTo
  const resolvedImages = images && images.length > 0 ? images : [{ src: imageSrc ?? '', alt: imageAlt ?? '' }]
  const hasMultipleImages = resolvedImages.length > 1

  const [carouselApi, setCarouselApi] = useState<CarouselApi>()

  useEffect(() => {
    if (!(carouselApi && carouselAutoplay && hasMultipleImages)) {
      return
    }
    const id = setInterval(() => {
      carouselApi.scrollNext()
    }, 5000)
    return () => clearInterval(id)
  }, [carouselApi, carouselAutoplay, hasMultipleImages])

  const { className: imageClassName, style: imageStyle, ...imageRest } = imageProps ?? {}

  const positionOverride: CSSProperties =
    imageObjectPosition !== undefined && imageObjectPosition !== '' ? { objectPosition: imageObjectPosition } : {}
  const mergedImage: CSSProperties = { ...(imageStyle as CSSProperties | undefined), ...positionOverride }
  const mergedImageStyle = Object.keys(mergedImage).length > 0 ? mergedImage : undefined
  const { className: eyebrowClassName, ...eyebrowRest } = eyebrowProps ?? {}
  const { className: headingClassName, ...headingRest } = headingProps ?? {}
  const { className: bodyClassName, ...bodyRest } = bodyProps ?? {}
  const { className: footerClassName, ...footerRest } = footerProps ?? {}
  const { className: footerLinkClassName, ...footerLinkRest } = footerLinkProps ?? {}

  const mediaBlock = (
    <div
      className={cn(
        'overflow-hidden rounded-xl border shadow-2xl/90',
        'order-2',
        isImageLeft ? 'md:order-1' : 'md:order-2'
      )}
    >
      {hasMultipleImages ? (
        <Carousel className="w-full" opts={{ loop: true }} setApi={setCarouselApi}>
          <div className="relative">
            {carouselShowControls ? (
              <CarouselPrevious className={carouselNavButtonClass} size="icon-sm" variant="outline" />
            ) : null}
            <CarouselContent className="ml-0">
              {resolvedImages.map((image, index) => (
                <CarouselItem className="pl-0" key={`${image.src}-${index}`}>
                  <div className={mediaImageFrameClass}>
                    <img
                      alt={image.alt}
                      className={cn(mediaImgClass, imageClassName)}
                      decoding="async"
                      height={512}
                      src={image.src}
                      style={mergedImageStyle}
                      width={512}
                      {...imageRest}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {carouselShowControls ? (
              <CarouselNext className={carouselNavButtonClassNext} size="icon-sm" variant="outline" />
            ) : null}
            <SplitFeatureCarouselDots slideCount={resolvedImages.length} />
          </div>
        </Carousel>
      ) : imagePicture ? (
        <div className={mediaImageFrameClass}>
          <picture className="absolute inset-0 size-full">
            {Object.entries(imagePicture.sources).map(([format, srcSet]) => (
              <source key={format} sizes={imagePictureSizes} srcSet={srcSet} type={`image/${format}`} />
            ))}
            <img
              alt={resolvedImages[0]?.alt}
              className={cn(mediaImgClass, imageClassName)}
              decoding="async"
              height={imagePicture.img.h}
              sizes={imagePictureSizes}
              src={imagePicture.img.src}
              style={mergedImageStyle}
              width={imagePicture.img.w}
              {...imageRest}
            />
          </picture>
        </div>
      ) : (
        <div className={mediaImageFrameClass}>
          <img
            alt={resolvedImages[0]?.alt}
            className={cn(mediaImgClass, imageClassName)}
            decoding="async"
            height={281}
            src={resolvedImages[0]?.src}
            style={mergedImageStyle}
            width={500}
            {...imageRest}
          />
        </div>
      )}
    </div>
  )

  const copyBlock = (
    <div className={cn('flex flex-col items-start gap-4', 'order-1', isImageLeft ? 'md:order-2' : 'md:order-1')}>
      <p
        className={cn('font-black text-primary text-sm uppercase tracking-[0.2em]', eyebrowClassName)}
        {...eyebrowRest}
      >
        {eyebrow}
      </p>
      <h2 className={cn('font-extrabold text-5xl leading-none', headingClassName)} {...headingRest}>
        {heading}
      </h2>
      <div className={cn('mb-4 max-w-lg text-lg text-muted-foreground leading-relaxed', bodyClassName)} {...bodyRest}>
        {body}
      </div>
      {footer ? (
        <div className={cn(footerClassName)} {...footerRest}>
          {footer}
        </div>
      ) : null}
      {hasDefaultFooter ? (
        <div className={cn(footerClassName)} {...footerRest}>
          <Link
            className={cn('group/footer-link inline-flex items-center gap-2 font-bold', footerLinkClassName)}
            to={footerTo}
            {...footerLinkRest}
          >
            <span className="border-primary border-b-2 pb-1 transition-[padding] duration-300 group-hover/footer-link:pr-4">
              {footerLabel}
            </span>
            <ArrowRightIcon className="size-[18px] text-primary" />
          </Link>
        </div>
      ) : null}
    </div>
  )

  return (
    <div className="mx-auto grid max-w-[88rem] grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:gap-16">
      {mediaBlock}
      {copyBlock}
    </div>
  )
}
